import { useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { api, queryKeys } from "../../api";
import { authStore, useAuth } from "../../auth/store";
import { PasskeyCancelledError, createPasskey, getPasskey, passkeysSupported } from "../../auth/webauthn";
import { BusyButton, ConfirmDialog, CopyButton, DataTable, Dialog, Field, PageHeader, useToast } from "../../components/ui";
import { formatDateTime } from "../../lib/format";
import { parseUA } from "../../lib/ua";
import { passwordProblems } from "../auth/SignupPage";
import { DirtyActions } from "../../components/UnsavedChanges";
import { Skeleton } from "../../components/States";

function deviceName(userAgent: string): string {
  const { browser, os } = parseUA(userAgent);
  if (browser === "Unknown" && os === "Unknown") return "Unknown device";
  if (browser === "Unknown") return os;
  if (os === "Unknown") return browser;
  return `${browser} on ${os}`;
}

type ProfileTab = "profile" | "password" | "two-factor" | "sessions";
const TABS: Array<{ id: ProfileTab; label: string }> = [
  { id: "profile", label: "Profile" },
  { id: "password", label: "Password" },
  { id: "two-factor", label: "Two-factor" },
  { id: "sessions", label: "Sessions" },
];

export function ProfilePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: ProfileTab; setupMfa?: string };
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(auth.me?.user.name ?? "");
  const [savedName, setSavedName] = useState(auth.me?.user.name ?? "");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwErr, setPwErr] = useState<string | null>(null);
  const mfa = useQuery({ queryKey: queryKeys.mfa, queryFn: api.me.mfa });
  const sessions = useQuery({ queryKey: queryKeys.loginSessions, queryFn: api.me.sessions });
  const startingTotp = useRef(false);
  const nameDirty = name !== savedName;

  // TOTP setup dialog
  const [totp, setTotp] = useState<{ secret: string; otpauthUrl: string; qrSvg: string } | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const [turningOffTotp, setTurningOffTotp] = useState(false);
  const [proof, setProof] = useState("");
  const hasPassword = auth.me?.user.hasPassword !== false;
  const totpOn = !!mfa.data?.totpEnabled;
  const passkeyCount = mfa.data?.passkeys.length ?? 0;
  const proofMissing = hasPassword ? proof.length === 0 : totpOn ? proof.replace(/\s/g, "").length < 6 : false;
  const removalProof = async () => {
    if (hasPassword) return { password: proof };
    if (totpOn) return { code: proof.replace(/\s+/g, "") };
    const opts = await api.me.passkeyStepUpOptions();
    const { challengeId, credential } = await getPasskey(opts);
    return { challengeId, credential };
  };
  const proofRejected = (e: unknown) => {
    if (e instanceof PasskeyCancelledError) toast.push("info", "Confirmation cancelled.");
    else toast.push("error", (e as Error).message);
  };

  const startTotp = async () => {
    if (startingTotp.current || totp) return;
    startingTotp.current = true;
    try {
      setTotp(await api.me.totpStart());
    } catch (e) {
      toast.push("error", (e as Error).message);
    } finally {
      startingTotp.current = false;
    }
  };
  const confirmTotp = useMutation({
    mutationFn: () => api.me.totpConfirm(totpCode.replace(/\s+/g, "")),
    onSuccess: (r) => {
      setRecovery(r.recoveryCodes);
      setTotp(null);
      setTotpCode("");
      authStore.clearNext("setup_mfa");
      void qc.invalidateQueries({ queryKey: queryKeys.mfa });
      toast.push("success", "Authenticator app enabled.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const disableTotp = useMutation({
    mutationFn: async () => api.me.totpDisable(await removalProof()),
    onSuccess: () => {
      setTurningOffTotp(false);
      setProof("");
      void qc.invalidateQueries({ queryKey: queryKeys.mfa });
      toast.push("info", "Authenticator app disabled.");
    },
    onError: proofRejected,
  });
  const addPasskey = useMutation({
    mutationFn: async () => {
      const opts = await api.me.passkeyRegisterOptions();
      const { challengeId, credential } = await createPasskey(opts);
      const label = window.prompt("Name this passkey (e.g. MacBook Touch ID)") || "Passkey";
      return api.me.passkeyRegisterVerify(challengeId, credential, label);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.mfa });
      toast.push("success", "Passkey added.");
    },
    onError: (e) => {
      if (e instanceof PasskeyCancelledError) toast.push("info", "Passkey setup cancelled.");
      else toast.push("error", (e as Error).message);
    },
  });
  const removePasskey = useMutation({
    mutationFn: async (id: string) => api.me.passkeyDelete(id, await removalProof()),
    onSuccess: () => {
      setRemoving(null);
      setProof("");
      void qc.invalidateQueries({ queryKey: queryKeys.mfa });
      toast.push("success", "Passkey removed.");
    },
    onError: proofRejected,
  });
  const saveName = useMutation({
    mutationFn: () => api.me.patch({ name }),
    onSuccess: () => {
      setSavedName(name);
      toast.push("success", "Profile saved.");
      void authStore.load();
    },
  });
  const changePw = useMutation({
    mutationFn: () => api.me.password(pw.current, pw.next),
    onSuccess: () => {
      setPw({ current: "", next: "", confirm: "" });
      toast.push("success", "Password changed. Other sessions were signed out.");
    },
    onError: (e) => setPwErr((e as Error).message),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.me.revokeSession(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.loginSessions });
      toast.push("success", "Session signed out.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const revokeOthers = useMutation({
    mutationFn: api.me.revokeOtherSessions,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.loginSessions });
      toast.push("success", "Other sessions were signed out.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const otherSessions = (sessions.data ?? []).some((s) => !s.current);

  const submitPw = (e: FormEvent) => {
    e.preventDefault();
    const problem = passwordProblems(pw.next);
    if (problem) return setPwErr(problem);
    if (pw.next !== pw.confirm) return setPwErr("Passwords do not match.");
    setPwErr(null);
    changePw.mutate();
  };

  const tab: ProfileTab = search.setupMfa ? "two-factor" : TABS.some((item) => item.id === search.tab) ? (search.tab as ProfileTab) : "profile";
  const setTab = (next: ProfileTab) => void navigate({ to: "/settings/profile", search: next === "profile" ? {} : { tab: next }, replace: true });

  return (
    <>
      <PageHeader
        title="Profile"
        subtitle={auth.me?.user.email}
        actions={tab === "sessions" && otherSessions ? (
          <BusyButton className="btn" busy={revokeOthers.isPending} busyLabel="Signing out…" onClick={() => revokeOthers.mutate()}>
            Sign out all
          </BusyButton>
        ) : undefined}
      >
        <nav className="section-tabs" aria-label="Profile sections">
          {TABS.map((item) => (
            <button key={item.id} type="button" className={item.id === tab ? "active" : undefined} aria-current={item.id === tab ? "page" : undefined} onClick={() => setTab(item.id)}>
              {item.label}
            </button>
          ))}
        </nav>
      </PageHeader>

      {tab === "profile" && (
        <div className="card form-card">
          <section className="section">
            <Field label="Name">{(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 360 }} />}</Field>
            <DirtyActions dirty={nameDirty} onDiscard={() => setName(savedName)}>
              <button className="btn primary" onClick={() => saveName.mutate()} disabled={saveName.isPending || !name.trim() || !nameDirty}>
                Save
              </button>
            </DirtyActions>
          </section>
        </div>
      )}

      {tab === "password" && (
        <div className="card form-card">
          <form className="section" onSubmit={submitPw} noValidate autoComplete="off" style={{ maxWidth: 420 }}>
            <Field label="Current password">{(p) => <input {...p} className="input" type="password" autoComplete="off" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />}</Field>
            <Field label="New password" hint="At least 12 characters.">
              {(p) => <input {...p} className="input" type="password" autoComplete="off" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />}
            </Field>
            <Field label="Confirm new password" error={pwErr}>
              {(p) => <input {...p} className="input" type="password" autoComplete="off" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />}
            </Field>
            <button className="btn primary" type="submit" disabled={changePw.isPending || !pw.current || !pw.next}>
              Change password
            </button>
          </form>
        </div>
      )}

      {tab === "two-factor" && (
        <div className="mfa-panel">
          <div className="mfa-intro">
            <span className="badge warn">Required</span>
            <p>Every account keeps at least one second factor. A passkey is the recommended method.</p>
          </div>
          {mfa.isPending ? (
            <Skeleton lines={3} />
          ) : (
            <div className="mfa-methods">
              <article className="mfa-method">
                <div className="mfa-method-copy">
                  <h2>Authenticator app</h2>
                  <p>A code from an app when you sign in.</p>
                  {mfa.data?.totpEnabled && <p className="mfa-meta">{mfa.data.recoveryCodesRemaining} recovery codes left</p>}
                </div>
                <div className="mfa-method-action">
                  {mfa.data?.totpEnabled ? (
                    <>
                      <span className="badge ok">On</span>
                      {passkeyCount > 0 ? (
                        <button className="btn" type="button" onClick={() => { setProof(""); setTurningOffTotp(true); }}>
                          Turn off
                        </button>
                      ) : (
                        <span className="muted small">Add a passkey before turning this off.</span>
                      )}
                    </>
                  ) : (
                    <button className="btn primary" type="button" onClick={() => void startTotp()}>
                      Set up
                    </button>
                  )}
                </div>
              </article>
              <article className="mfa-method">
                <div className="mfa-method-copy">
                  <h2>Passkeys</h2>
                  <p>Sign in with this device.</p>
                </div>
                <div className="mfa-method-action">
                  {passkeysSupported() ? (
                    <button className="btn primary" type="button" onClick={() => addPasskey.mutate()} disabled={addPasskey.isPending}>
                      Add a passkey
                    </button>
                  ) : (
                    <span className="muted small">Passkeys are not supported in this browser.</span>
                  )}
                </div>
                {(mfa.data?.passkeys.length ?? 0) > 0 && (
                  <ul className="mfa-passkeys">
                    {mfa.data?.passkeys.map((k) => (
                      <li key={k.id}>
                        <span className="mfa-passkey-name">{k.name}</span>
                        <span className="muted small">Added {formatDateTime(k.createdAt)}</span>
                        {passkeyCount > 1 || totpOn ? (
                          <button
                            className="btn ghost"
                            type="button"
                            onClick={() => { setProof(""); setRemoving({ id: k.id, name: k.name }); }}
                            aria-label={`Remove passkey ${k.name}`}
                          >
                            Remove
                          </button>
                        ) : (
                          <span className="muted small">Required</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            </div>
          )}
        </div>
      )}

      {tab === "sessions" && (
        <div className="card stack-table login-sessions">
          <DataTable
            ariaLabel="Active sessions"
            rows={sessions.data ?? []}
            loading={sessions.isPending}
            rowKey={(s) => s.id}
            columns={[
              { key: "ua", header: "Device", className: "session-device", render: (s) => <span className="session-device-name" title={s.userAgent}>{deviceName(s.userAgent)}{s.current && <span className="badge ok">this device</span>}</span> },
              { key: "ip", header: "IP", label: "IP", className: "session-ip", render: (s) => <span className="mono">{s.ip}</span> },
              { key: "seen", header: "Last active", label: "Last active", render: (s) => formatDateTime(s.lastSeenAt) },
              { key: "act", header: "", align: "right", className: "session-action", render: (s) => (s.current ? null : <button className="btn ghost sm" onClick={() => revoke.mutate(s.id)}>Sign out</button>) },
            ]}
          />
        </div>
      )}

      <ConfirmDialog
        open={!!removing}
        danger
        title="Remove passkey"
        confirmLabel={removePasskey.isPending ? "Removing…" : "Remove passkey"}
        busy={removePasskey.isPending}
        disabled={proofMissing}
        onConfirm={() => removing && removePasskey.mutate(removing.id)}
        onClose={() => { setRemoving(null); setProof(""); }}
      >
        <p>{removing?.name} will no longer sign you in.</p>
        {removalProofField(hasPassword, totpOn, proof, setProof)}
      </ConfirmDialog>
      <ConfirmDialog
        open={turningOffTotp}
        danger
        title="Turn off authenticator app"
        confirmLabel={disableTotp.isPending ? "Turning off…" : "Turn off"}
        busy={disableTotp.isPending}
        disabled={proofMissing}
        onConfirm={() => disableTotp.mutate()}
        onClose={() => { setTurningOffTotp(false); setProof(""); }}
      >
        <p>Codes from your authenticator app will stop working, and its recovery codes are discarded.</p>
        {removalProofField(hasPassword, totpOn, proof, setProof)}
      </ConfirmDialog>
      <Dialog open={!!totp} onClose={() => setTotp(null)} title="Set up your authenticator app" footer={<><button className="btn" onClick={() => setTotp(null)}>Cancel</button><button className="btn primary" onClick={() => confirmTotp.mutate()} disabled={totpCode.replace(/\s/g, "").length < 6 || confirmTotp.isPending}>Confirm</button></>}>
        {totp && (
          <>
            <p className="muted">Scan the QR code with 1Password, Google Authenticator, Authy, or any TOTP app, then enter the code it shows.</p>
            <div className="qr" dangerouslySetInnerHTML={{ __html: totp.qrSvg }} />
            <details style={{ margin: "12px 0" }}>
              <summary className="muted small">Can't scan? Enter the key manually</summary>
              <div className="mono-box" style={{ marginTop: 6 }}>
                {totp.secret} <CopyButton value={totp.secret} />
              </div>
            </details>
            <Field label="6-digit code">{(p) => <input {...p} className="input" inputMode="numeric" autoComplete="off" value={totpCode} onChange={(e) => setTotpCode(e.target.value)} autoFocus />}</Field>
          </>
        )}
      </Dialog>

      <Dialog open={!!recovery} onClose={() => setRecovery(null)} title="Save your recovery codes" footer={<button className="btn primary" onClick={() => setRecovery(null)}>I've saved them</button>}>
        <p className="muted">Each code works once. Store them somewhere safe; they are the only way back in if you lose your authenticator.</p>
        <div className="code-list">{recovery?.map((c) => <span key={c}>{c}</span>)}</div>
        <div style={{ marginTop: 8 }}>
          <CopyButton value={(recovery ?? []).join("\n")} label="Copy all" />
        </div>
      </Dialog>
    </>
  );
}

function removalProofField(hasPassword: boolean, totpOn: boolean, proof: string, setProof: (value: string) => void) {
  if (hasPassword) {
    return (
      <Field label="Password">
        {(props) => <input {...props} className="input" type="password" autoComplete="current-password" value={proof} onChange={(event) => setProof(event.target.value)} />}
      </Field>
    );
  }
  if (totpOn) {
    return (
      <Field label="Authenticator code">
        {(props) => <input {...props} className="input" inputMode="numeric" autoComplete="one-time-code" value={proof} onChange={(event) => setProof(event.target.value)} />}
      </Field>
    );
  }
  return <p>Confirm with a passkey to continue.</p>;
}
